import { Alert } from '@/components/alert';
import { Form, OneTimePasswordInput } from '@/components/form';
import { Flex } from '@/components/layouting';
import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import { Typography } from '@/components/typography';
import { ButtonAnt } from '@/components/buttons/button/ButtonAnt';
import { Link } from '@/components/link';
import { Page as messages } from '../messages';
import { useAuthContext } from '@/contexts/AuthContext';
import { TwoFactorStep } from '../enums/two-factor-step';
import type { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { useDialogWithExtraProps } from '@/components/dialog/useDialogWithExtraProps';
import { BackupCodesModal, type BackupCodesModalProps } from './BackupCodesModal';

const VERIFY_FORM_ID = 'verify-form';

const { Headline, Paragraph } = Typography;

interface VerifyFormValues {
  code: string;
}

interface VerifyContentProps {
  backupCodes?: string[];
  requireSetup?: boolean;
  onStepChange: (step: TwoFactorStep) => void;
}

export const VerifyContent: FC<VerifyContentProps> = ({ backupCodes, requireSetup, onStepChange }) => {
  const [form] = Form.useForm<VerifyFormValues>();
  const history = useHistory();

  const {
    verifyTotpAsync,
    loadSession,
    loading: { isLoadingVerifyTotp },
    error: { verifyTotp: verifyTotpError },
  } = useAuthContext();

  const { openDialog: openBackupCodesModal, dialog: backupCodesModal } =
    useDialogWithExtraProps<BackupCodesModalProps>(BackupCodesModal);

  const hasBackupCodes = backupCodes && backupCodes.length > 0;

  const INITIAL_VALUES: VerifyFormValues = {
    code: '',
  };

  const handleSubmit = async ({ code }: VerifyFormValues) => {
    try {
      await verifyTotpAsync({ code, trustDevice: false });

      if (hasBackupCodes) {
        // Setup flow: show backup codes first, then load session + redirect
        openBackupCodesModal({
          backupCodes,
          onContinue: () => {
            loadSession(undefined, true);
          },
        });
      } else {
        // Normal login flow: load session + redirect immediately
        await loadSession(undefined, true);
      }
    } catch (e: unknown) {
      console.error('Error verifying TOTP code:', e);
    }
  };

  const handleBack = () => {
    if (requireSetup) {
      onStepChange(TwoFactorStep.Setup);
    } else {
      history.goBack();
    }
  };

  return (
    <>
      {backupCodesModal}
      <Form id={VERIFY_FORM_ID} form={form} onFinish={handleSubmit} initialValues={INITIAL_VALUES}>
        <Flex gap={24} vertical>
          <Flex gap={16} vertical>
            {verifyTotpError ? (
              <Alert
                showIcon
                description={<FormattedMessage {...messages.alertMessage} />}
                type="error"
                data-cy="verify-error-alert"
              />
            ) : (
              <>
                <Headline>
                  <FormattedMessage {...messages.title} />
                </Headline>
                <Paragraph data-cy="verify-description">
                  <FormattedMessage {...messages.description} />
                </Paragraph>
              </>
            )}

            <Form.Item colon={false} data-cy="verify-code" name="code" rules={[{ required: true }]}>
              <OneTimePasswordInput autoFocus />
            </Form.Item>

            <Flex gap={8} vertical>
              <ButtonAnt
                block
                form={VERIFY_FORM_ID}
                data-cy="verify-submit-button"
                htmlType="submit"
                size="large"
                type="primary"
                loading={isLoadingVerifyTotp}
              >
                <FormattedMessage {...messages.login} />
              </ButtonAnt>
              <ButtonAnt block data-cy="verify-cancel-button" onClick={handleBack} size="large">
                <FormattedMessage {...messages.back} />
              </ButtonAnt>
            </Flex>
          </Flex>
          <Paragraph>
            <FormattedMessage {...messages.lostDevice} />{' '}
            <Link to="/lost-device" data-cy="lost-device-link">
              <FormattedMessage {...messages.recoverAccess} />
            </Link>
          </Paragraph>
        </Flex>
      </Form>
    </>
  );
};
